// Resolves the map / Places API language code from a trip's
// `map_label_mode` and current trip state.
//
//   'mine'  → viewer's browser language (Intl.DateTimeFormat-normalized).
//   'local' → derived from trip region:
//                1. activeCity.regionCode, if present.
//                2. Single-region consensus across cities + POIs.
//                3. Fallback to navigator.language.
//
// Returns an ISO 639-1 code (or extended like "zh-CN", "pt-BR")
// suitable for both Google Maps JS `language=` URL param and the
// Places API `languageCode` field.

import type { MapLabelMode, PoiView, TripCityView } from '~/shared/api.js';

const REGION_TO_LANG: Record<string, string> = {
  JP: 'ja',
  KR: 'ko',
  CN: 'zh-CN',
  TW: 'zh-TW',
  HK: 'zh-HK',
  MO: 'zh-MO',
  PT: 'pt-PT',
  BR: 'pt-BR',
  ES: 'es',
  MX: 'es',
  AR: 'es',
  FR: 'fr',
  DE: 'de',
  IT: 'it',
  NL: 'nl',
  PL: 'pl',
  RU: 'ru',
  TR: 'tr',
  TH: 'th',
  VN: 'vi',
  ID: 'id',
  MY: 'ms',
  PH: 'tl',
  SE: 'sv',
  NO: 'no',
  DK: 'da',
  FI: 'fi',
  IL: 'iw',
  SA: 'ar',
  AE: 'ar',
  EG: 'ar',
  IN: 'hi',
  US: 'en',
  GB: 'en',
  CA: 'en',
  AU: 'en',
  NZ: 'en',
  IE: 'en',
  SG: 'en',
};

function regionToLang(region: string | null): string {
  if (!region) return 'en';
  return REGION_TO_LANG[region.toUpperCase()] ?? 'en';
}

/**
 * Normalize a browser-style `navigator.language` tag into a code
 * Google Maps and Places API understand. Most tags pass through after
 * a primary-subtag chop; Chinese and Brazilian/European Portuguese
 * need explicit handling because their extended forms carry meaning
 * Google respects.
 */
export function normalizeBrowserLanguage(tag: string): string {
  const lower = tag.toLowerCase();
  if (lower.startsWith('zh-cn') || lower.startsWith('zh-hans')) return 'zh-CN';
  if (lower.startsWith('zh-tw') || lower.startsWith('zh-hant')) return 'zh-TW';
  if (lower.startsWith('zh-hk')) return 'zh-HK';
  if (lower.startsWith('pt-br')) return 'pt-BR';
  if (lower.startsWith('pt-pt') || lower === 'pt') return 'pt-PT';
  const primary = lower.split('-')[0];
  return primary || 'en';
}

export interface ResolveLanguageInput {
  mode: MapLabelMode;
  activeCity: TripCityView | null;
  cities: TripCityView[];
  pois: PoiView[];
  navigatorLanguage: string;
}

export function resolveLanguage(input: ResolveLanguageInput): string {
  if (input.mode === 'mine') {
    return normalizeBrowserLanguage(input.navigatorLanguage);
  }
  // 'local' mode
  if (input.activeCity) {
    return regionToLang(input.activeCity.regionCode);
  }
  // No focused city — find a single-region consensus across cities + POIs.
  const regions = new Set<string>();
  for (const c of input.cities) {
    if (c.regionCode) regions.add(c.regionCode.toUpperCase());
  }
  for (const p of input.pois) {
    if (p.regionCode) regions.add(p.regionCode.toUpperCase());
  }
  if (regions.size === 1) {
    const [region] = [...regions];
    return regionToLang(region ?? null);
  }
  // Empty trip or multi-region trip viewed without a focus city —
  // fall back to the viewer's browser language.
  return normalizeBrowserLanguage(input.navigatorLanguage);
}
